import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { SignupSchema } from '@redpandev/shared';
import {
  Avatar,
  Badge,
  Button,
  Field,
  IconButton,
  Input,
  useReducedMotion,
  type MascotMood,
} from '@redpandev/ui';
import { useAuth } from '../auth/AuthContext';
import { safeReturnTo, withReturnTo } from '../auth/returnTo';
import { api, ApiError } from '../lib/api';
import { AvatarPicker } from '../components/AvatarPicker';
import { avatarSources, randomAvatarId } from '../lib/avatars';
import { AuthCard, authLink } from './auth/AuthCard';
import { FormAlert } from './auth/FormAlert';
import { PasswordStrengthMeter } from './auth/PasswordStrengthMeter';
import { SocialAuthButtons } from './auth/SocialAuthButtons';

const MESSAGES: Record<string, string> = {
  email_taken: 'Cet email est déjà utilisé.',
  displayname_taken: 'Ce pseudo est déjà pris.',
  validation_error: 'Vérifie les champs du formulaire.',
};

/** Petit temps de fête (mascotte celebrate) avant la redirection. */
const CELEBRATE_DELAY_MS = 600;

export function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // `?returnTo=` VALIDÉ (anti open-redirect) : funnel invité du Motus du jour
  // (spec games/motus-daily §9.1) — après signup, retour sur la page du jeu où
  // le serveur adopte la partie invitée (spec §6.1).
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const reduced = useReducedMotion();
  const [values, setValues] = useState({ email: '', password: '', displayName: '' });
  // Avatar présélectionné AU HASARD (spec 07 §2.5) — l'utilisateur peut en
  // changer (grille pliable) ou re-tirer au sort (🎲). Jamais vide au submit.
  const [avatarId, setAvatarId] = useState<string>(() => randomAvatarId());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  /** Résultat de la vérif de dispo du pseudo (null = pas encore vérifié). */
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null);
  // États d'humeur de la mascotte (purement décoratifs, feedback texte à côté).
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [editedSinceError, setEditedSinceError] = useState(false);
  const [shakeNonce, setShakeNonce] = useState(0);

  const mood: MascotMood = celebrating
    ? 'celebrate'
    : passwordFocused
      ? 'shy'
      : serverError && !editedSinceError
        ? 'sad'
        : 'happy';

  const set = (k: keyof typeof values) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setEditedSinceError(true);
    if (k === 'displayName') setNameAvailable(null);
  };

  async function checkName() {
    const name = values.displayName.trim();
    if (name.length < 3) return;
    try {
      const { available } = await api.checkDisplayName(name);
      setNameAvailable(available);
      setErrors((prev) => ({
        ...prev,
        displayName: available ? undefined : 'Ce pseudo est déjà pris.',
      }));
    } catch {
      /* la vérif finale se fait côté serveur au signup */
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(undefined);
    setServerError(false);
    setEditedSinceError(false);
    const parsed = SignupSchema.safeParse({ ...values, avatarId });
    if (!parsed.success) {
      const f = parsed.error.flatten().fieldErrors;
      setErrors({
        email: f.email?.[0],
        password: f.password?.[0],
        displayName: f.displayName?.[0],
      });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await signup(parsed.data);
      setCelebrating(true);
      if (!reduced) await new Promise((r) => setTimeout(r, CELEBRATE_DELAY_MS));
      navigate(returnTo ?? '/profile', { replace: true });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'error';
      if (code === 'email_taken') setErrors((p) => ({ ...p, email: MESSAGES.email_taken }));
      else if (code === 'displayname_taken')
        setErrors((p) => ({ ...p, displayName: MESSAGES.displayname_taken }));
      else setFormError(MESSAGES[code] ?? 'Une erreur est survenue. Réessaie.');
      setServerError(true);
      setShakeNonce((n) => n + 1);
      setSubmitting(false);
    }
  }

  const showAvailable = nameAvailable === true && !errors.displayName;

  return (
    <AuthCard
      title="Rejoins la meute !"
      mood={mood}
      shakeNonce={shakeNonce}
      footer={
        <>
          Déjà un compte ? {/* Le returnTo suit le joueur entre signup et login (funnel invité). */}
          <Link to={returnTo ? withReturnTo('/login', returnTo) : '/login'} className={authLink}>
            Connecte-toi !
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormAlert>{formError}</FormAlert>
        <div>
          <Field
            label="Pseudo"
            error={errors.displayName}
            hint={showAvailable ? undefined : '3 à 20 caractères : lettres, chiffres, - et _'}
          >
            <Input
              name="displayName"
              autoComplete="username"
              placeholder="RedPanda_42"
              value={values.displayName}
              onChange={set('displayName')}
              onBlur={checkName}
              required
            />
          </Field>
          {showAvailable && (
            <p role="status" className="mt-1.5">
              <Badge intent="success">✓ Ce pseudo est libre !</Badge>
            </p>
          )}
        </div>

        {/* Choix d'avatar : présélection au hasard, dé 🎲 pour re-tirer,
            grille pliable pour ne pas écraser le formulaire (spec 07 §2.5). */}
        <div>
          <p className="mb-1.5 text-sm font-semibold text-ink-100">Choisis ta trombine !</p>
          <div className="flex items-center gap-3">
            <Avatar {...avatarSources(avatarId)} name={`Avatar ${avatarId}`} size={56} />
            <IconButton
              label="Tirer un avatar au hasard"
              variant="secondary"
              onClick={() => setAvatarId(randomAvatarId())}
            >
              <span aria-hidden="true">🎲</span>
            </IconButton>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-expanded={pickerOpen}
              onClick={() => setPickerOpen((o) => !o)}
            >
              {pickerOpen ? 'Fermer la galerie' : 'Voir toute la galerie'}
            </Button>
          </div>
          {pickerOpen && (
            <AvatarPicker
              className="mt-3"
              value={avatarId}
              onChange={(id) => {
                if (id) setAvatarId(id);
              }}
              label="Choisir un avatar"
            />
          )}
        </div>

        <Field label="Email" error={errors.email}>
          <Input
            name="email"
            type="email"
            autoComplete="email"
            placeholder="toi@exemple.fr"
            value={values.email}
            onChange={set('email')}
            required
          />
        </Field>
        <div>
          <Field label="Mot de passe" error={errors.password} hint="12 caractères minimum">
            <Input
              name="password"
              type="password"
              autoComplete="new-password"
              value={values.password}
              onChange={set('password')}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              required
            />
          </Field>
          <PasswordStrengthMeter password={values.password} />
        </div>
        <Button type="submit" size="lg" loading={submitting} className="w-full">
          C&apos;est parti !
        </Button>
      </form>
      {/* Boutons sociaux (spec 11 §13) — rien si la feature est dark (flags off). */}
      <div className="mt-5">
        <SocialAuthButtons returnTo={returnTo} />
      </div>
    </AuthCard>
  );
}
